import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { showNotification } from '../utils/notifications';
import { getDeployments, createDeployment, triggerDeployment, getDeployment, deleteDeployment, reactivateDeployment, deactivateDeployment, downloadArtifacts } from '../services/apiService';
import '../styles/deployments.css';

function DeploymentPage() {
  const { showToast } = useToast();
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [subdomain, setSubdomain] = useState('');
  const [superadminEmail, setSuperadminEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [accountExpiresAt, setAccountExpiresAt] = useState('');

  // Change to track loading state per button
  const [loadingButtons, setLoadingButtons] = useState({}); // Format: {`${deploymentId}-${type}`: boolean}
  const [loadingDeployments, setLoadingDeployments] = useState({}); // Track loading state per deployment
  const [reactivatingDeployments, setReactivatingDeployments] = useState({}); // Track reactivation state
  const [refreshing, setRefreshing] = useState(false); // Track refresh button state
  const [deactivatingDeployments, setDeactivatingDeployments] = useState({}); // Track deactivation state

  const getLoadingKey = (deploymentId, type) => `${deploymentId}-${type}`;

  // Get tomorrow's date for minimum date input
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  useEffect(() => {
    // Initial load
    loadDeployments();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDeployments().finally(() => {
      setRefreshing(false);
    });
  };

  const loadDeployments = async () => {
    try {
      setLoading(true);
      const response = await getDeployments();
      
      if (response && response.success && Array.isArray(response.result)) {
        setDeployments(response.result);
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load deployments:', error);
      setError(error.message || 'Failed to load deployments');
      setDeployments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStepStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'deployed':
      case 'completed':
        return 'success';
      case 'in progress':
        return 'warning';
      case 'failed':
        return 'danger';
      case 'pending':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleDeploymentClick = (deployment) => {
    setSelectedDeployment(deployment);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subdomain) {
      showToast('Please enter a subdomain', 'error');
      return;
    }

    if (!superadminEmail) {
      showToast('Please enter superadmin email', 'error');
      return;
    }

    if (!clientName) {
      showToast('Please enter a client name', 'error');
      return;
    }
    
    if (!accountExpiresAt) {
      showToast('Please select an expiry date', 'error');
      return;
    }

    try {
      setSubmitting(true);
      const response = await createDeployment({
        subdomain: subdomain.trim().toLowerCase(),
        adminEmail: superadminEmail,
        clientName,
        accountExpiresAt
      });

      if (response.success) {
        showToast('Deployment created successfully!', 'success');
        setShowModal(false);
        loadDeployments();
        // Reset form
        setSubdomain('');
        setSuperadminEmail('');
        setClientName('');
        setAccountExpiresAt('');
      } else {
        throw new Error(response.data?.messages?.message || 'Failed to create deployment');
      }
    } catch (error) {
      console.error('Error creating deployment:', error);
      const isString = typeof error;
      showNotification(isString ? error : 'Failed to create deployment','error');
    } finally {
      setSubmitting(false);
    }
  };

  const getDeploymentById = async (deploymentId) => {
    try {
      const response = await getDeployment(deploymentId);
      if (response && response.success) {
        setDeployments(prevDeployments => 
          prevDeployments.map(dep => 
            dep.id === deploymentId ? response.result : dep
          )
        );
      }
    } catch (error) {
      console.error('Failed to fetch deployment:', error);
      showToast(error.message, 'error');
    }
  };

  const handleAction = async (deploymentId, type) => {
    const loadingKey = getLoadingKey(deploymentId, type);
    try {
      // Set loading state for this specific button
      setLoadingButtons(prev => ({ ...prev, [loadingKey]: true }));

      console.log("type",type);
      if(type.includes('artifact')){
        await downloadArtifacts(deploymentId, type);
        showToast(`${type} artifact downloaded successfully`, 'success');
      }
      else{
        await triggerDeployment(deploymentId, type);
        showToast(`${type} deployment triggered successfully`, 'success');
      }
      
      // Fetch updated status for this deployment only
      await getDeploymentById(deploymentId);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      // Clear loading state for this specific button
      setLoadingButtons(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleDelete = async (deploymentId) => {
    if (!window.confirm('Are you sure you want to delete this deployment?')) {
      return;
    }

    try {
      setLoadingDeployments(prev => ({ ...prev, [deploymentId]: true }));
      await deleteDeployment(deploymentId);
      showToast('Deployment deleted successfully', 'success');
      loadDeployments(); // Refresh the list
    } catch (error) {
      showToast(error.message || 'Failed to delete deployment', 'error');
    } finally {
      setLoadingDeployments(prev => ({ ...prev, [deploymentId]: false }));
    }
  };

  const handleReactivate = async (deploymentId) => {
    if (!window.confirm('Are you sure you want to reactivate this deployment?')) {
      return;
    }

    try {
      setReactivatingDeployments(prev => ({ ...prev, [deploymentId]: true }));
      await reactivateDeployment(deploymentId);
      showToast('Deployment reactivated successfully', 'success');
      loadDeployments(); // Refresh the list
    } catch (error) {
      showToast(error.message || 'Failed to reactivate deployment', 'error');
    } finally {
      setReactivatingDeployments(prev => ({ ...prev, [deploymentId]: false }));
    }
  };

  const handleDeactivate = async (deploymentId) => {
    if (!window.confirm('Are you sure you want to deactivate this deployment?')) {
      return;
    }

    try {
      setDeactivatingDeployments(prev => ({ ...prev, [deploymentId]: true }));
      await deactivateDeployment(deploymentId);
      showToast('Deployment deactivated successfully', 'success');
      loadDeployments(); // Refresh the list
    } catch (error) {
      showToast(error.message || 'Failed to deactivate deployment', 'error');
    } finally {
      setDeactivatingDeployments(prev => ({ ...prev, [deploymentId]: false }));
    }
  };

  const getActionButton = (status, type, deploymentId, deployment) => {
    const loadingKey = getLoadingKey(deploymentId, type);
    const isLoading = loadingButtons[loadingKey];
    const isDeactivated = deployment.account_deactivated_at !== null;
    const isReactivating = reactivatingDeployments[deployment.id];

    // If deployment is deactivated, disable all buttons
    if (isDeactivated) {
      return (
        <button className="btn btn-status deactivated" disabled>
          <i className="fas fa-ban"></i> Deactivated
        </button>
      );
    }

    // Check if button should be disabled based on dependencies
    const isDisabled = () => {
      if (type === 'frontend' && deployment.backend_status !== 'deployed') {
        return true;
      }
      if (type === 'hosting' && deployment.frontend_status !== 'deployed') {
        return true;
      }
      if (type === 'mobile' && deployment.hosting_status !== 'deployed') {
        return true;
      }
      if (type === 'mobile-artifact' && deployment.mobile_status !== 'deployed' && deployment.mobile_artifact_id == null) {
        return true;
      }
      return false;
    };

    if (status === 'deployed') {
      return (
        <button className="btn btn-status deployed" disabled>
          <i className="fas fa-check-circle"></i> Deployed
        </button>
      );
    }
    
    if (status === 'deploying' || isLoading) {
      return (
        <button className="btn btn-status deploying" disabled>
          <i className="fas fa-spinner fa-spin"></i> {isLoading ? 'Triggering...' : type === 'mobile' ? 'Building...' : 'Deploying'}
        </button>
      );
    }
    
    if (status === 'failed') {
      return (
        <button 
          className="btn btn-status retry"
          onClick={() => handleAction(deploymentId, type)}
          disabled={isLoading || isDisabled()}
        >
          <i className="fas fa-redo"></i> Retry
        </button>
      );
    }

    if(type === 'mobile-artifact'){

      return (
        <button 
          className="btn btn-status build"
          onClick={() => handleAction(deploymentId, type)}
          disabled={isLoading || isDisabled()}
        >
          <i className="fas fa-download"></i> Download APK
        </button>
      );

    }



    
    return (
      <button 
        className="btn btn-status build"
        onClick={() => handleAction(deploymentId, type)}
        disabled={isLoading || isDisabled()}
      >
        <i className="fas fa-play"></i> Build
      </button>
    );
  };

  // Calculate days until expiry and format it nicely
  const getExpiryText = (expiryDate) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time part for accurate day comparison
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0); // Reset time part for accurate day comparison
    
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Account expires Today (${formatDate(expiryDate)})`;
    } else if (diffDays === 1) {
      return `Account expires Tomorrow (${formatDate(expiryDate)})`;
    } else if (diffDays > 0) {
      return `Account expires in ${diffDays} days (${formatDate(expiryDate)})`;
    } else {
      return `Account expired ${Math.abs(diffDays)} days ago (${formatDate(expiryDate)})`;
    }
  };

  return (
    <div className="deployments-page">
      <div className="page-header">
        <h1 className="page-title">Deployments</h1>
        <div className="page-actions">
          <button 
            className="btn btn-secondary"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <i className={`fas fa-sync ${refreshing ? 'fa-spin' : ''}`}></i>
            {refreshing ? ' Refreshing...' : ' Refresh'}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowModal(true)}
          >
            <i className="fas fa-plus"></i> Create Deployment
          </button>
        </div>
      </div>

      <div className="deployments-container">
        {loading && !refreshing ? (
          <div className="loading-state">
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
            <p>Loading deployments...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={loadDeployments}>
              Try Again
            </button>
          </div>
        ) : deployments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="fas fa-rocket"></i>
            </div>
            <h3>No Active Deployments</h3>
            <p>Create a new demo account to start deployment</p>
          </div>
        ) : (
          <div className="deployments-grid">
            {deployments.map(deployment => {
              const isDeactivated = deployment.account_deactivated_at !== null;
              const isReactivating = reactivatingDeployments[deployment.id];
              console.log("deactivated",isDeactivated);
              
              return (
                <div 
                  key={deployment.id} 
                  className={`deployment-card ${isDeactivated ? 'deactivated' : ''}`}
                >
                  {isDeactivated && (
                    <div className="deactivated-banner">
                      <i className="fas fa-ban"></i> Account Deactivated
                    </div>
                  )}
                  
                  <div className="deployment-header">
                    <div className="deployment-title">
                      <div className="client-info">
                        <i className="fas fa-building"></i>
                        <h3>Trailio Asset Shield - {deployment.client_name}</h3>
                      </div>
                      <div className="app-info">
                        <span className="app-name">
                          <i className="fas fa-user"></i>
                          <span>Admin</span>
                        </span>
                        <span>{deployment.admin_email}</span>
                      </div>
                    </div>
                    
                    <div className="deployment-domain">
                      <div className="domain-wrapper">
                        <i className="fas fa-globe"></i>
                        <code className="domain-text">demo.{deployment.subdomain}.assetshield.trailio.io</code>
                      </div>
                      <button 
                        className="btn btn-icon" 
                        onClick={() => {
                          navigator.clipboard.writeText(`demo.${deployment.subdomain}.assetshield.trailio.io`);
                          showToast('Domain copied to clipboard!', 'success');
                        }}
                        title="Copy domain"
                      >
                        <i className="far fa-copy"></i>
                      </button>
                    </div>
                  </div>

                  <div className="deployment-steps">
                    <div className="step-row">
                      <div className="step-info">
                        <i className="fas fa-server"></i>
                        <span>Backend</span>
                      </div>
                      {getActionButton(deployment.backend_status, 'backend', deployment.id, deployment)}
                    </div>

                    <div className="step-row">
                      <div className="step-info">
                        <i className="fas fa-desktop"></i>
                        <span>Frontend</span>
                      </div>
                      {getActionButton(deployment.frontend_status, 'frontend', deployment.id, deployment)}
                    </div>

                    <div className="step-row">
                      <div className="step-info">
                        <i className="fas fa-globe"></i>
                        <span>Hosting</span>
                      </div>
                      {getActionButton(deployment.hosting_status, 'hosting', deployment.id, deployment)}
                    </div>

                    <div className="step-row">
                      <div className="step-info">
                        <i className="fas fa-mobile"></i>
                        <span>Mobile</span>
                      </div>
                      {getActionButton(deployment.mobile_status, 'mobile', deployment.id, deployment)}
                    </div>

                    <div className="step-row">
                      <div className="step-info">
                        <i className="fas fa-mobile"></i>
                        <span>Mobile Artifact</span>
                      </div>
                      {getActionButton(null, 'mobile-artifact', deployment.id, deployment)}
                    </div>



                    <div className="deployment-actions">
                      <div className="action-buttons-row">
                        <button
                          className="btn btn-danger btn-full"
                          onClick={() => handleDelete(deployment.id)}
                          disabled={loadingDeployments[deployment.id]}
                        >
                          {loadingDeployments[deployment.id] ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i> Deleting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-trash"></i> Delete Deployment
                            </>
                          )}
                        </button>
                      </div>
                      
                     { deployment.status === 'deployed' && (<div className="action-buttons-row">
                        
                        {isDeactivated ? (
                          <button
                            className="btn btn-success btn-full"
                            onClick={() => handleReactivate(deployment.id)}
                            disabled={reactivatingDeployments[deployment.id]}
                          >
                            {reactivatingDeployments[deployment.id] ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i> Reactivating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-redo-alt"></i> Reactivate Deployment
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            className="btn btn-warning btn-full"
                            onClick={() => handleDeactivate(deployment.id)}
                            disabled={deactivatingDeployments[deployment.id]}
                          >
                            {deactivatingDeployments[deployment.id] ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i> Deactivating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-ban"></i> Deactivate Deployment
                              </>
                            )}
                          </button>
                        )}
                      </div>
            )}
                      
                      {deployment.account_expires_at && (
                        <div className={`expiry-info ${isDeactivated ? 'deactivated' : ''}`}>
                          <i className={isDeactivated ? "fas fa-ban" : "fas fa-calendar-alt"}></i>
                          <span>
                            {isDeactivated 
                              ? `Account deactivated on ${formatDate(deployment.account_deactivated_at)}`
                              : getExpiryText(deployment.account_expires_at)
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Create New Deployment</h2>
                <button 
                  type="button"
                  className="modal-close" 
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="clientName">Client Name</label>
                    <input
                      type="text"
                      id="clientName"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Enter client name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subdomain">Subdomain</label>
                    <div className="subdomain-input-group">
                      <span className="subdomain-prefix">demo.</span>
                      <input
                        type="text"
                        id="subdomain"
                        className="subdomain-input"
                        value={subdomain}
                        onChange={(e) => setSubdomain(e.target.value)}
                        placeholder="Enter subdomain"
                        required
                        disabled={submitting}
                      />
                      <span className="subdomain-suffix">.assetshield.trailio.io</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="superadminEmail">Superadmin Email</label>
                    <div className="input-group">
                      <i className="fas fa-envelope input-icon"></i>
                      <input
                        type="email"
                        id="superadminEmail"
                        value={superadminEmail}
                        onChange={(e) => setSuperadminEmail(e.target.value)}
                        placeholder="Enter superadmin email"
                        required
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="accountExpiresAt">Account Expires At</label>
                    <input
                      type="date"
                      id="accountExpiresAt"
                      value={accountExpiresAt}
                      onChange={(e) => setAccountExpiresAt(e.target.value)}
                      min={getTomorrowDate()}
                      required
                    />
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => setShowModal(false)}
                      disabled={submitting}
                    >
                      <i className="fas fa-times"></i> Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check"></i> Create Deployment
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeploymentPage;